import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Tabs, Tab,
  TextField, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";

import {
  fetchItType,
  deleteItType,
  updateItType,
  addItTypeCriteria,
  deleteItTypeCriteria,
  addItTypeCriteriaValue,
  deleteItTypeCriteriaValue,
} from "../../api/companyApi";
import { wrapTextCell } from "../../utils/gridUtils";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";
import KpiConstraintAlert from "../../components/common/KpiConstraintAlert";
import KpiDetailDialog from "../../components/common/KpiDetailDialog";
import { useKpiUsageStatus } from "../../hooks/useKpiUsageStatus";

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function ItTypeDetailPage() {
  const { businessDomainId, itTypeId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── ItKpi state ──
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [kpiEditMode, setKpiEditMode] = useState(false);
  const [kpiOriginalName, setKpiOriginalName] = useState(null);
  const [kpiForm, setKpiForm] = useState({
    indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "",
  });
  const [kpiConfirmOpen, setKpiConfirmOpen] = useState(false);
  const [kpiDeleteTarget, setKpiDeleteTarget] = useState(null);
  const [kpiDetailOpen, setKpiDetailOpen] = useState(false);
  const [kpiDetailRow, setKpiDetailRow] = useState(null);

  // ── Criteria state ──
  const [criteriaDialogOpen, setCriteriaDialogOpen] = useState(false);
  const [criteriaForm, setCriteriaForm] = useState({ classificationCriteriaCd: "", classificationCriteriaName: "" });
  const [criteriaConfirmOpen, setCriteriaConfirmOpen] = useState(false);
  const [criteriaDeleteTarget, setCriteriaDeleteTarget] = useState(null);

  // ── Criteria Value state ──
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [cvDialogOpen, setCvDialogOpen] = useState(false);
  const [cvForm, setCvForm] = useState({ classificationCriteriaValueCd: "", classificationCriteriaValueName: "" });
  const [cvConfirmOpen, setCvConfirmOpen] = useState(false);
  const [cvDeleteTarget, setCvDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    try { setData(await fetchItType(itTypeId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itTypeId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Delete IT type ──
  const handleDelete = async () => {
    try {
      await deleteItType(itTypeId);
      notify.success(t("message.delete.success"));
      setConfirmOpen(false);
      navigate(`/design/${businessDomainId}/it-types`, { replace: true });
    } catch (e) { notify.apiError(e); }
  };

  // ── ItKpi CRUD ──
  const openKpiAdd = () => {
    setKpiEditMode(false);
    setKpiOriginalName(null);
    setKpiForm({ indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "" });
    setKpiDialogOpen(true);
  };
  const openKpiEdit = (row) => {
    setKpiEditMode(true);
    setKpiOriginalName(row.indicatorName);
    setKpiForm({
      indicatorName: row.indicatorName,
      unit: row.unit || "",
      measuringMethod: row.measuringMethod || "",
      evaluationStandard: row.evaluationStandard || "",
      description: row.description || "",
    });
    setKpiDialogOpen(true);
  };
  const handleKpiSave = async () => {
    if (!kpiForm.indicatorName) { notify.error(t("message.error.required")); return; }
    const indicators = data.indicators || [];
    if (!kpiEditMode || kpiOriginalName !== kpiForm.indicatorName) {
      if (indicators.some((ind) => ind.indicatorName === kpiForm.indicatorName)) {
        notify.error(t("itKpi.error.duplicate")); return;
      }
    }
    const updatedIndicators = kpiEditMode
      ? indicators.map((ind) => ind.indicatorName === kpiOriginalName ? { ...kpiForm } : ind)
      : [...indicators, { ...kpiForm }];
    try {
      await updateItType({ ...data, indicators: updatedIndicators });
      notify.success(kpiEditMode ? t("message.update.success") : t("message.create.success"));
      setKpiDialogOpen(false);
      loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleKpiDelete = async () => {
    const updatedIndicators = (data.indicators || []).filter((ind) => ind.indicatorName !== kpiDeleteTarget);
    try {
      await updateItType({ ...data, indicators: updatedIndicators });
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setKpiConfirmOpen(false); setKpiDeleteTarget(null);
  };

  // ── Criteria CRUD ──
  const handleCriteriaSave = async () => {
    if (!criteriaForm.classificationCriteriaCd || !criteriaForm.classificationCriteriaName) {
      notify.error(t("message.error.required")); return;
    }
    try {
      await addItTypeCriteria(itTypeId, { ...criteriaForm, createdAt: new Date().toISOString() });
      notify.success(t("message.create.success"));
      setCriteriaDialogOpen(false); loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleCriteriaDelete = async () => {
    try {
      await deleteItTypeCriteria(itTypeId, criteriaDeleteTarget);
      notify.success(t("message.delete.success")); loadData();
    } catch (e) { notify.apiError(e); }
    setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null);
  };

  // ── Criteria Value CRUD ──
  const handleCvSave = async () => {
    if (!cvForm.classificationCriteriaValueCd || !cvForm.classificationCriteriaValueName) {
      notify.error(t("message.error.required")); return;
    }
    try {
      await addItTypeCriteriaValue(itTypeId, {
        classificationCriteria: selectedCriteria,
        classificationCriteriaValueCd: cvForm.classificationCriteriaValueCd,
        classificationCriteriaValueName: cvForm.classificationCriteriaValueName,
        createdValueAt: new Date().toISOString(),
      });
      notify.success(t("message.create.success"));
      setCvDialogOpen(false); loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleCvDelete = async () => {
    try {
      await deleteItTypeCriteriaValue(itTypeId, cvDeleteTarget);
      notify.success(t("message.delete.success")); loadData();
    } catch (e) { notify.apiError(e); }
    setCvConfirmOpen(false); setCvDeleteTarget(null);
  };

  const { usedKpiNames } = useKpiUsageStatus(businessDomainId);

  if (!data) return null;

  const indicators = data.indicators || [];
  const hasLockedKpi = indicators.some((ind) => usedKpiNames.has(ind.indicatorName));
  const criterias = data.classificationCriterias || [];
  const criteriaValues = selectedCriteria
    ? (data.classificationCriteriaValues || []).filter(
        (v) => v.classificationCriteria?.classificationCriteriaName === selectedCriteria.classificationCriteriaName
      )
    : [];

  // ── Column definitions ──
  const kpiColumns = [
    { field: "indicatorName", headerName: t("appKpi.indicatorName"), flex: 1, minWidth: 160,
      renderCell: (p) => {
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{p.row.indicatorName}
          {isLocked && <Chip icon={<LockIcon fontSize="small" />} label={t("common.inUse")} size="small" color="warning" variant="outlined" />}
        </Box>);
      },
    },
    { field: "unit", headerName: t("appKpi.unit"), width: 100 },
    { field: "measuringMethod", headerName: t("appKpi.measuringMethod"), flex: 1, minWidth: 140, renderCell: wrapTextCell },
    { field: "evaluationStandard", headerName: t("appKpi.evaluationStandard"), flex: 1, minWidth: 140, renderCell: wrapTextCell },
    {
      field: "actions", headerName: t("common.actions"), width: 120, sortable: false,
      renderCell: (p) => {
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (<Box>
          <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}><span>
            <IconButton size="small" disabled={isLocked} onClick={(e) => { e.stopPropagation(); openKpiEdit(p.row); }}><EditIcon fontSize="small" /></IconButton>
          </span></Tooltip>
          <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}><span>
            <IconButton size="small" color="error" disabled={isLocked} onClick={(e) => { e.stopPropagation(); setKpiDeleteTarget(p.row.indicatorName); setKpiConfirmOpen(true); }}><DeleteIcon fontSize="small" /></IconButton>
          </span></Tooltip>
        </Box>);
      },
    },
  ];

  const criteriaColumns = [
    { field: "classificationCriteriaCd", headerName: t("itCriteria.code"), width: 160 },
    { field: "classificationCriteriaName", headerName: t("itCriteria.name"), flex: 1, minWidth: 200 },
    {
      field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => {
          e.stopPropagation();
          setCriteriaDeleteTarget({ classificationCriteriaCd: p.row.classificationCriteriaCd, classificationCriteriaName: p.row.classificationCriteriaName });
          setCriteriaConfirmOpen(true);
        }}><DeleteIcon fontSize="small" /></IconButton>
      ),
    },
  ];
  const cvColumns = [
    { field: "classificationCriteriaValueCd", headerName: t("criteriaValue.code"), width: 160 },
    { field: "classificationCriteriaValueName", headerName: t("criteriaValue.name"), flex: 1, minWidth: 200 },
    {
      field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setCvDeleteTarget(p.row); setCvConfirmOpen(true); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      {hasLockedKpi && <KpiConstraintAlert variant="type-locked" />}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("itType.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/design/${businessDomainId}/it-types/${itTypeId}/edit`)}>{t("common.edit")}</Button>
          <Tooltip title={hasLockedKpi ? t("kpi.constraint.type.locked") : ""}><span>
            <Button variant="contained" color="error" startIcon={<DeleteIcon />} disabled={hasLockedKpi} onClick={() => setConfirmOpen(true)}>{t("common.delete")}</Button>
          </span></Tooltip>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("itType.name")} value={data.itTypeName} /></Grid>
          <Grid size={12}><DetailField label={t("itType.description")} value={data.itTypeDescription} /></Grid>
        </Grid>
      </DetailSection>

      <Box sx={{ mt: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabIndex} onChange={(_, v) => { setTabIndex(v); setSelectedCriteria(null); }}>
          <Tab label={t("itKpi.title")} />
          <Tab label={t("itCriteria.title")} />
        </Tabs>
      </Box>

      {/* Tab 0: ItKpi */}
      <TabPanel value={tabIndex} index={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("itKpi.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openKpiAdd}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={indicators}
          columns={kpiColumns}
          getRowId={(r) => r.indicatorName}
          key={indicators.map((i) => i.indicatorName).join(",")}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          onRowClick={(params) => { setKpiDetailRow(params.row); setKpiDetailOpen(true); }}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </TabPanel>

      {/* Tab 1: ItCriteria */}
      <TabPanel value={tabIndex} index={1}>
        {selectedCriteria ? (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <IconButton onClick={() => setSelectedCriteria(null)} size="small"><ArrowBackIcon /></IconButton>
              <Typography variant="subtitle1">{selectedCriteria.classificationCriteriaName}：{t("criteriaValue.list.title")}</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" size="small" startIcon={<AddIcon />}
                onClick={() => { setCvForm({ classificationCriteriaValueCd: "", classificationCriteriaValueName: "" }); setCvDialogOpen(true); }}>
                {t("common.add")}
              </Button>
            </Box>
            <DataGrid rows={criteriaValues} columns={cvColumns}
              getRowId={(r) => r.classificationCriteriaValueCd}
              key={criteriaValues.map((v) => v.classificationCriteriaValueCd).join(",")}
              autoHeight pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick />
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle1">{t("itCriteria.list.title")}</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />}
                onClick={() => { setCriteriaForm({ classificationCriteriaCd: "", classificationCriteriaName: "" }); setCriteriaDialogOpen(true); }}>
                {t("common.add")}
              </Button>
            </Box>
            <DataGrid rows={criterias} columns={criteriaColumns}
              getRowId={(r) => r.classificationCriteriaCd}
              key={criterias.map((c) => c.classificationCriteriaCd).join(",")}
              autoHeight pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick
              onRowClick={(params) => setSelectedCriteria(params.row)}
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        )}
      </TabPanel>

      <Box sx={{ mt: 3 }}><Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/it-types`)}>{t("common.back")}</Button></Box>

      {/* Confirm Dialogs */}
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={kpiConfirmOpen} onConfirm={handleKpiDelete} onCancel={() => { setKpiConfirmOpen(false); setKpiDeleteTarget(null); }} />
      <ConfirmDialog open={criteriaConfirmOpen} onConfirm={handleCriteriaDelete} onCancel={() => { setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null); }} />
      <ConfirmDialog open={cvConfirmOpen} onConfirm={handleCvDelete} onCancel={() => { setCvConfirmOpen(false); setCvDeleteTarget(null); }} />

      {/* KPI Detail Dialog */}
      <KpiDetailDialog open={kpiDetailOpen} onClose={() => { setKpiDetailOpen(false); setKpiDetailRow(null); }} indicator={kpiDetailRow} />

      {/* ItKpi Add/Edit Dialog */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{kpiEditMode ? t("itKpi.edit.title") : t("itKpi.create.title")}</DialogTitle>
        <DialogContent>
          <KpiConstraintAlert variant={kpiEditMode ? (usedKpiNames.has(kpiForm.indicatorName) ? "edit-locked" : "edit-unused") : "create"} sx={{ mt: 1 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required label={t("appKpi.indicatorName")} value={kpiForm.indicatorName}
              onChange={(e) => setKpiForm({ ...kpiForm, indicatorName: e.target.value })} />
            <TextField fullWidth label={t("appKpi.unit")} value={kpiForm.unit}
              onChange={(e) => setKpiForm({ ...kpiForm, unit: e.target.value })} />
            <TextField fullWidth multiline rows={2} label={t("appKpi.measuringMethod")} value={kpiForm.measuringMethod}
              onChange={(e) => setKpiForm({ ...kpiForm, measuringMethod: e.target.value })} />
            <TextField fullWidth multiline rows={2} label={t("appKpi.evaluationStandard")} value={kpiForm.evaluationStandard}
              onChange={(e) => setKpiForm({ ...kpiForm, evaluationStandard: e.target.value })} />
            <TextField fullWidth multiline rows={2} label={t("appKpi.description")} value={kpiForm.description}
              onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleKpiSave} disabled={kpiEditMode && usedKpiNames.has(kpiForm.indicatorName)}>
            {kpiEditMode ? t("common.save") : t("common.add")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Criteria Add Dialog */}
      <Dialog open={criteriaDialogOpen} onClose={() => setCriteriaDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("itCriteria.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("itCriteria.code")} value={criteriaForm.classificationCriteriaCd}
              onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("itCriteria.name")} value={criteriaForm.classificationCriteriaName}
              onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCriteriaDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCriteriaSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>

      {/* Criteria Value Add Dialog */}
      <Dialog open={cvDialogOpen} onClose={() => setCvDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("criteriaValue.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("criteriaValue.code")} value={cvForm.classificationCriteriaValueCd}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("criteriaValue.name")} value={cvForm.classificationCriteriaValueName}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCvDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCvSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
